import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { apiClient } from '../api/client';

interface User {
  id: number;
  username: string;
  email?: string;
  is_admin: boolean;
  has_consented: boolean;
  has_completed_survey: boolean;
  study_group?: string;
  role?: string;
  institution?: string;
  confirmation1?: number | null;
  confirmation2?: number | null;
  intervention1?: number | null;
  intervention2?: number | null;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (username: string, password: string) => Promise<void>;
  register: (data: any) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(() => {
    const savedToken = localStorage.getItem('token');
    // Set the header immediately during initialization (before useEffect runs)
    if (savedToken) {
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
      console.log('🔐 AuthContext initialized with token from localStorage');
    }
    return savedToken;
  });

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete apiClient.defaults.headers.common['Authorization'];
  }, []);

  const fetchCurrentUser = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/auth/me');
      setUser(response.data);
    } catch (error) {
      logout();
    }
  }, [logout]);

  useEffect(() => {
    if (token) {
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    }
  }, [token, fetchCurrentUser]);

  const login = async (username: string, password: string) => {
    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);

    const response = await apiClient.post('/api/auth/login', formData);
    const { access_token } = response.data;

    localStorage.setItem('token', access_token);
    setToken(access_token);
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
  };

  const register = async (data: any) => {
    await apiClient.post('/api/auth/register', data);
  };

  const refreshUser = async () => {
    if (token) {
      await fetchCurrentUser();
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, isAuthenticated: !!token, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};
